// ─── Écran LISTES : tâches / courses partagées (calque plein écran) ──
// Porté de MoPCal (`screens/ListesScreen.js`), première coupe — même
// va-et-vient liste↔détail DANS UN SEUL FICHIER que EcranEDT.js (grilles↔
// détail) : pas de navigation empilée, juste un état `listeId` qui bascule
// la vue. Swipe ✏️/🗑️ sur les cartes, tap = ouvrir. Coche pour rayer.
// Laissé pour un prochain incrément (cf. services/listes.js) : droits par
// personne, photos, justificatifs, lien vers le catalogue d'ingrédients.
import React, { useEffect, useMemo, useState } from 'react';
import { View, Text, TextInput, TouchableOpacity, ScrollView, StyleSheet } from 'react-native';
import {
  PageHeader, EmptyState, LigneSwipe, GesteRetourEcran, Feuille, HIT,
  useUI, useT, useAuth, confirmerSuppression,
} from '@mop/core';
import {
  EMOJIS_LISTE, ecouterListes, creerListe, renommerListe, supprimerListe,
  ecouterItems, ajouterItem, toggleItem, supprimerItem,
} from './services/listes';

export default function EcranListes({ calId, onFermer }) {
  const UI = useUI();
  const t = useT();
  const { user } = useAuth();
  const s = useMemo(() => makeStyles(UI), [UI]);
  const auteur = { uid: user?.uid, nom: user?.prenom || user?.email || '' };

  const [listes, setListes] = useState([]);
  const [erreurListes, setErreurListes] = useState('');
  const [listeId, setListeId] = useState(null);   // null = liste des listes
  const [items, setItems] = useState([]);
  const [erreurItems, setErreurItems] = useState('');
  const [feuilleListe, setFeuilleListe] = useState(null);   // { initial } | null
  const [nomItem, setNomItem] = useState('');
  const [qteItem, setQteItem] = useState('');
  const [uniteItem, setUniteItem] = useState('');

  useEffect(() => {
    if (!calId) { setListes([]); return undefined; }
    setErreurListes('');
    return ecouterListes(calId, setListes, setErreurListes);
  }, [calId]);

  useEffect(() => {
    if (!listeId) { setItems([]); return undefined; }
    setErreurItems('');
    return ecouterItems(listeId, setItems, setErreurItems);
  }, [listeId]);

  const liste = listes.find(l => l.id === listeId) || null;

  const enregistrerListe = async ({ titre, emoji }) => {
    try {
      if (feuilleListe?.initial) await renommerListe(feuilleListe.initial.id, { titre, emoji });
      else await creerListe({ calendrierId: calId, titre, emoji, creePar: auteur.uid, creeParNom: auteur.nom });
    } catch (_) {}
    setFeuilleListe(null);
  };
  const supprimerL = (l) => confirmerSuppression(t('listes.supprListeTitre'), t('listes.supprListeMsg', { titre: l.titre }),
    async () => { try { await supprimerListe(l.id); if (listeId === l.id) setListeId(null); } catch (_) {} });

  const ajouter = async () => {
    const texte = nomItem.trim();
    if (!texte || !listeId) return;
    setNomItem(''); setQteItem(''); setUniteItem('');
    try { await ajouterItem(listeId, { texte, quantite: qteItem, unite: uniteItem, creePar: auteur.uid }); } catch (_) {}
  };
  const restants = items.filter(i => !i.fait);
  const coches = items.filter(i => i.fait);

  // ── Vue LISTE DES LISTES ──
  if (!listeId) {
    return (
      <GesteRetourEcran actif={!!onFermer} onRetour={onFermer}>
        <View style={s.page}>
          <PageHeader titre={t('listes.titre')} iconeTitre="📝" onRetour={onFermer}
            action={{ icone: '＋', label: t('listes.nouvelleListe'), onPress: () => setFeuilleListe({ initial: null }) }} />
          <ScrollView contentContainerStyle={s.corps}>
            {!!erreurListes && (
              <View style={s.bandeau}>
                <Text style={s.bandeauTxt}>⚠️ {t('listes.erreurLectureListes')}</Text>
                <Text style={s.bandeauDet}>{erreurListes}</Text>
              </View>
            )}
            {erreurListes ? null : listes.length === 0 ? (
              <EmptyState icon="📝" texte={t('listes.aucuneListe')} />
            ) : listes.map(l => (
              <LigneSwipe key={l.id} espacement={8} enabled
                onEditer={() => setFeuilleListe({ initial: l })}
                onSupprimer={() => supprimerL(l)}>
                <TouchableOpacity style={s.carte} activeOpacity={0.7} onPress={() => setListeId(l.id)}>
                  <Text style={{ fontSize: 20 }}>{l.emoji || '📝'}</Text>
                  <View style={{ flex: 1 }}>
                    <Text style={s.carteNom} numberOfLines={1}>{l.titre}</Text>
                    <Text style={s.carteSous}>
                      {(l.nbItems || 0) === 0 ? t('listes.listeVide')
                        : (((l.nbItems || 0) - (l.nbFaits || 0)) === 1
                          ? t('listes.compteurSingulier', { restants: (l.nbItems || 0) - (l.nbFaits || 0), total: l.nbItems || 0 })
                          : t('listes.compteurPluriel', { restants: (l.nbItems || 0) - (l.nbFaits || 0), total: l.nbItems || 0 }))}
                    </Text>
                  </View>
                </TouchableOpacity>
              </LigneSwipe>
            ))}
          </ScrollView>

          <Feuille visible={!!feuilleListe} titre={feuilleListe?.initial ? t('listes.renommer') : t('listes.nouvelleListe')}
            emoji="📝" onFermer={() => setFeuilleListe(null)}>
            <FormListe initial={feuilleListe?.initial} onEnregistrer={enregistrerListe} t={t} UI={UI} />
          </Feuille>
        </View>
      </GesteRetourEcran>
    );
  }

  // ── Vue DÉTAIL d'une liste ──
  return (
    <GesteRetourEcran actif onRetour={() => setListeId(null)}>
      <View style={s.page}>
        <PageHeader titre={liste?.titre || t('listes.titre')} iconeTitre={liste?.emoji || '📝'} onRetour={() => setListeId(null)} />
        <View style={s.ajoutBarre}>
          <TextInput style={[s.input, { flex: 2 }]} value={nomItem} onChangeText={setNomItem}
            placeholder={t('listes.phAjouterItem')} placeholderTextColor={UI.faint}
            onSubmitEditing={ajouter} returnKeyType="done" />
          <TextInput style={[s.input, { flex: 0.7 }]} value={qteItem} onChangeText={setQteItem}
            placeholder={t('listes.phQuantite')} placeholderTextColor={UI.faint} />
          <TextInput style={[s.input, { flex: 0.8 }]} value={uniteItem} onChangeText={setUniteItem}
            placeholder={t('listes.phUnite')} placeholderTextColor={UI.faint} />
          <TouchableOpacity style={s.ajouterBtn} onPress={ajouter} hitSlop={HIT} disabled={!nomItem.trim()}>
            <Text style={[s.ajouterTxt, !nomItem.trim() && { opacity: 0.4 }]}>＋</Text>
          </TouchableOpacity>
        </View>

        <ScrollView contentContainerStyle={s.corps}>
          {!!erreurItems && (
            <View style={s.bandeau}>
              <Text style={s.bandeauTxt}>⚠️ {t('listes.erreurLectureItems')}</Text>
              <Text style={s.bandeauDet}>{erreurItems}</Text>
            </View>
          )}
          {erreurItems ? null : items.length === 0 ? (
            <EmptyState icon={liste?.emoji || '📝'} texte={t('listes.listeVide')} />
          ) : (
            <>
              {restants.map(i => (
                <LigneItem key={i.id} item={i} s={s} UI={UI}
                  onToggle={() => toggleItem(listeId, i.id, true, auteur).catch(() => {})}
                  onSupprimer={() => supprimerItem(listeId, i.id, false).catch(() => {})} />
              ))}
              {coches.length > 0 && <Text style={s.sect}>{t('listes.nSelectionnes', { n: coches.length })}</Text>}
              {coches.map(i => (
                <LigneItem key={i.id} item={i} s={s} UI={UI}
                  onToggle={() => toggleItem(listeId, i.id, false, auteur).catch(() => {})}
                  onSupprimer={() => supprimerItem(listeId, i.id, true).catch(() => {})} />
              ))}
            </>
          )}
        </ScrollView>
      </View>
    </GesteRetourEcran>
  );
}

function LigneItem({ item, s, UI, onToggle, onSupprimer }) {
  return (
    <LigneSwipe espacement={8} enabled onSupprimer={onSupprimer}>
      <TouchableOpacity style={s.itemCarte} activeOpacity={0.7} onPress={onToggle}>
        <Text style={{ fontSize: 18 }}>{item.fait ? '✅' : '⬜'}</Text>
        <Text style={[s.itemTxt, item.fait && s.itemTxtFait]} numberOfLines={1}>
          {[item.quantite, item.unite, item.texte].filter(Boolean).join(' ')}
        </Text>
      </TouchableOpacity>
    </LigneSwipe>
  );
}

function FormListe({ initial, onEnregistrer, t, UI }) {
  const [titre, setTitre] = useState(initial?.titre || '');
  const [emoji, setEmoji] = useState(initial?.emoji || EMOJIS_LISTE[0]);
  useEffect(() => { setTitre(initial?.titre || ''); setEmoji(initial?.emoji || EMOJIS_LISTE[0]); }, [initial]);
  const fs = useMemo(() => makeFormStyles(UI), [UI]);
  return (
    <View>
      <TextInput style={fs.input} value={titre} onChangeText={setTitre}
        placeholder={t('listes.phNomListe')} placeholderTextColor={UI.faint} autoFocus />
      <View style={fs.emojis}>
        {EMOJIS_LISTE.map(e => (
          <TouchableOpacity key={e} style={[fs.emojiBtn, emoji === e && { borderColor: UI.accent, backgroundColor: UI.accentTint }]}
            onPress={() => setEmoji(e)}>
            <Text style={{ fontSize: 20 }}>{e}</Text>
          </TouchableOpacity>
        ))}
      </View>
      <TouchableOpacity style={[fs.valider, !titre.trim() && { opacity: 0.4 }]} disabled={!titre.trim()}
        onPress={() => onEnregistrer({ titre, emoji })}>
        <Text style={fs.validerTxt}>{t('commun.enregistrer')}</Text>
      </TouchableOpacity>
    </View>
  );
}
const makeFormStyles = (UI) => StyleSheet.create({
  input: { borderWidth: 0.5, borderColor: UI.champBord, backgroundColor: UI.champ, color: UI.ink, borderRadius: 10, paddingHorizontal: 12, paddingVertical: 11, fontSize: 15, marginBottom: 14 },
  emojis: { flexDirection: 'row', flexWrap: 'wrap', gap: 8, marginBottom: 16 },
  emojiBtn: { width: 44, height: 44, borderRadius: 10, borderWidth: 1, borderColor: UI.border, backgroundColor: UI.card, alignItems: 'center', justifyContent: 'center' },
  valider: { paddingVertical: 12, borderRadius: 10, alignItems: 'center', backgroundColor: UI.accent },
  validerTxt: { color: '#fff', fontWeight: '800', fontSize: 15 },
});

const makeStyles = (UI) => StyleSheet.create({
  page: { flex: 1, backgroundColor: UI.bg },
  corps: { padding: 12, paddingBottom: 30 },
  carte: { flexDirection: 'row', alignItems: 'center', gap: 12, backgroundColor: UI.card, borderRadius: 14, borderWidth: 0.5, borderColor: UI.border, padding: 13 },
  carteNom: { fontSize: 14.5, fontWeight: '700', color: UI.ink },
  carteSous: { fontSize: 12, color: UI.muted, marginTop: 1 },
  ajoutBarre: { flexDirection: 'row', gap: 6, paddingHorizontal: 12, paddingTop: 10, paddingBottom: 6, alignItems: 'center' },
  input: { backgroundColor: UI.champ, borderRadius: 10, borderWidth: 0.5, borderColor: UI.champBord, paddingHorizontal: 10, paddingVertical: 9, fontSize: 13.5, color: UI.ink },
  ajouterBtn: { width: 38, height: 38, borderRadius: 10, alignItems: 'center', justifyContent: 'center', backgroundColor: UI.accent },
  ajouterTxt: { color: '#fff', fontSize: 20, fontWeight: '700', lineHeight: 22 },
  itemCarte: { flexDirection: 'row', alignItems: 'center', gap: 12, backgroundColor: UI.card, borderRadius: 14, borderWidth: 0.5, borderColor: UI.border, padding: 13, marginBottom: 8 },
  itemTxt: { flex: 1, fontSize: 14.5, color: UI.ink, fontWeight: '600' },
  itemTxtFait: { color: UI.faint, textDecorationLine: 'line-through' },
  sect: { fontSize: 12, fontWeight: '800', color: UI.faint, textTransform: 'uppercase', letterSpacing: 0.5, marginTop: 16, marginBottom: 8 },
  bandeau: { backgroundColor: UI.dangerTint || '#fdecec', borderRadius: 12, borderWidth: 0.5, borderColor: UI.danger || '#e5484d', padding: 11, marginBottom: 10 },
  bandeauTxt: { fontSize: 13, fontWeight: '800', color: UI.danger || '#e5484d' },
  bandeauDet: { fontSize: 11.5, color: UI.muted, marginTop: 3 },
});
